import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '@env/environment';
import { LoginPayload, LoginResponse, User, UserCompany } from './user.model';

const TOKEN_KEY = 'idra_token';

/**
 * Auth basée sur les tokens Bearer de Laravel Sanctum (personal access tokens).
 * Ce choix — plutôt que les cookies de session — unifie le web et la future app mobile.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private readonly _user = signal<User | null>(null);
  private readonly _token = signal<string | null>(localStorage.getItem(TOKEN_KEY));

  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._token() !== null);

  /** Sociétés de l'utilisateur courant (multi-société). */
  readonly companies = computed<UserCompany[]>(() => this._user()?.companies ?? []);

  /** Société active, résolue depuis currentCompanyId (null si aucune). */
  readonly activeCompany = computed<UserCompany | null>(() => {
    const user = this._user();
    if (!user) {
      return null;
    }
    return user.companies.find((company) => company.id === user.currentCompanyId) ?? null;
  });

  get token(): string | null {
    return this._token();
  }

  login(payload: LoginPayload): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/auth/login`, { device_name: 'web', ...payload })
      .pipe(tap((res) => this.setSession(res)));
  }

  /**
   * Démarre la connexion Microsoft (Entra ID) : redirection complète vers le back,
   * qui gère le flux OAuth puis revient sur /auth/microsoft/callback#token=...
   */
  loginWithMicrosoft(): void {
    window.location.assign(`${environment.apiUrl}/auth/microsoft/redirect`);
  }

  /**
   * Termine une connexion externe (OAuth) : enregistre le token Sanctum
   * reçu du back puis charge l'utilisateur courant.
   */
  loginWithToken(token: string): Observable<User> {
    this._token.set(token);
    localStorage.setItem(TOKEN_KEY, token);
    return this.fetchMe();
  }

  /** Recharge l'utilisateur courant (ex. au démarrage de l'app si un token existe). */
  fetchMe(): Observable<User> {
    return this.http
      .get<User>(`${environment.apiUrl}/auth/me`)
      .pipe(tap((user) => this._user.set(user)));
  }

  logout(): void {
    this.http.post(`${environment.apiUrl}/auth/logout`, {}).subscribe({
      complete: () => this.clearSession(),
      error: () => this.clearSession(),
    });
  }

  /**
   * Invalide la session locale sans appel HTTP.
   * Utilisé par l'interceptor sur 401 : sans purge du token, authGuard
   * laisserait repasser l'utilisateur et créerait une boucle de redirection.
   */
  invalidateSession(): void {
    this.clearSession();
  }

  hasPermission(permission: string): boolean {
    return this._user()?.permissions.includes(permission) ?? false;
  }

  /**
   * Bascule la société active. Le back renvoie l'utilisateur à jour
   * (currentCompanyId) ; on met le signal à jour pour rafraîchir le contexte.
   */
  switchCompany(companyId: number): Observable<User> {
    return this.http
      .post<User>(`${environment.apiUrl}/companies/${companyId}/switch`, {})
      .pipe(tap((user) => this._user.set(user)));
  }

  private setSession(res: LoginResponse): void {
    this._token.set(res.token);
    this._user.set(res.user);
    localStorage.setItem(TOKEN_KEY, res.token);
  }

  private clearSession(): void {
    this._token.set(null);
    this._user.set(null);
    localStorage.removeItem(TOKEN_KEY);
    this.router.navigate(['/auth/login']);
  }
}
